import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
  viewChild,
} from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import {
  WebhookDialogComponent,
  WebhookDialogData,
} from '../webhook-dialog/webhook-dialog.component';
import { UiV2SelectorComponent } from '@ui-kit/ui-v2-selector/ui-v2-selector.component';
import { Store } from '@ngrx/store';
import { WorkflowWebhookActions } from '@states/workflow-webhooks/workflow-webhooks.action-type';
import { ActiveOrganization } from '@models/organization.model';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { WorkflowWebhookModel } from '@models/workflow-webhook.model';
import { WorkflowWebhooksSelectors } from '@states/workflow-webhooks/workflow-webhooks.selector-types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { routerSegments } from '@consts/routes';
import { Router } from '@angular/router';
import { ConfirmDialogService } from 'src/app/shared/confirm-dialog/confirm-dialog.service';
import { take } from 'rxjs';

@Component({
  selector: 'webhook-select',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    UiV2SelectorComponent,
  ],
  templateUrl: './webhook-select.component.html',
  styleUrl: './webhook-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebhookSelectComponent implements OnInit, AfterViewInit {
  public initValue: InputSignal<string> = input('');
  public webhookIdSelectOutput: OutputEmitterRef<string> = output();
  public disabled = input<boolean>(false);
  public disabledWebhooksMap: Signal<Map<string, boolean>> = input();

  public selectOrganizationSig: Signal<ActiveOrganization> =
    this.store$.selectSignal(OrganizationSelectors.selectActiveOrganization);
  public selectWebhooksSig: Signal<
    WorkflowWebhookModel.WebhookEndpointMongoDocument[]
  > = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectWorkflowWebhooks,
  );
  public selectIsLoadingSig: Signal<boolean> = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectIsLoading,
  );

  public selectSig = viewChild(UiV2SelectorComponent);

  public webhookId = new FormControl('');

  constructor(
    private dialog: MatDialog,
    private store$: Store,
    private destroyRef: DestroyRef,
    private router: Router,
    private confirmDialogService: ConfirmDialogService
  ) {
    effect(() => {
      const isLoading = this.selectIsLoadingSig();

      if (isLoading) {
        this.selectSig()?.selectedItem?.disable();
      } else if (!this.disabled()) {
        this.selectSig()?.selectedItem?.enable();
      }
    })
  }

  public ngOnInit(): void {
    const webhooks = this.selectWebhooksSig();

    if (this.initValue()) {
      this.webhookId.setValue(this.initValue());
    }

    const orgId = this.selectOrganizationSig().orgId as string;

    if (!webhooks?.length) {
      this.store$.dispatch(
        WorkflowWebhookActions.getWorkflowWebhooks({ orgId: orgId }),
      );
    }

    this.webhookId.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((webhookId) => {
        this.webhookIdSelectOutput.emit(webhookId);
      });
  }

  public ngAfterViewInit(): void {
    if (this.disabled()) {
      this.selectSig().selectedItem?.disable();
    }
  }

  public create(): void {
    this.dialog
      .open(WebhookDialogComponent, {
        panelClass: 'modal-no-padding',
        width: '760px',
        maxHeight: '80vh',
        data: null,
      })
      .afterClosed()
      .subscribe((dialogData: WebhookDialogData) => {
        this.webhookId.setValue(dialogData.webhookId);
        this.selectSig().matSelect()?.close();
      });
  }

  public manage(): void {
    const managePageRoute = `/${routerSegments.database}/${routerSegments.workflowWebhooks}`;
    
    this.confirmDialogService
      .discard()
      .pipe(take(1))
      .subscribe((leave) => {
        if (leave) {
          this.router.navigate([managePageRoute]);
        }
      });
  }

  public markTouched(): void {
    this.selectSig()?.selectedItem?.markAsTouched();
  }
}
